import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { useRouter } from 'next/router'
import { useReCaptcha } from 'next-recaptcha-v3'
import { Controller, useForm } from 'react-hook-form'
import {
  AddressAutocomplete,
  Button,
  Checkbox,
  Col,
  ControlledForm,
  ControlledInput,
  Fieldset,
  Icon,
  Image,
  Link,
  List,
  ListItem,
  Modal,
  Row,
  Select,
  Text
} from '@/client/components'
import { IconName } from '@/client/components/Icon/types'
import { useGetBranches, useGetCountries, useGetStates, useMutateSampleBook, useRouterPush } from '@/client/hooks'
import { IAddressSuggestion, StorageKey } from '@/client/types'
import { ROUTER_PATH, format } from '@/client/utils'
import { getStates } from '@/infra/services'
import { schemaResolver } from './asserts'
import * as S from './styles'
import { SampleBookFormSchema } from '../types'
import { ContactFormProps } from '../types'

export const ContactForm: FC<ContactFormProps> = ({
  contact_form,
  result,
  assert_text,
  first_side_box,
  second_side_box
}) => {
  const { routerPush, defaultLocale } = useRouterPush()
  const { locale } = useRouter()
  const queryClient = useQueryClient()

  const [formError, setFormError] = useState<string>('')
  const { executeRecaptcha } = useReCaptcha()
  const { mutate: mutateSampleBook } = useMutateSampleBook()
  const [isClosing, setIsClosing] = useState<boolean>(false)
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const { data: countriesData } = useGetCountries(locale)
  const { data: branchesData } = useGetBranches(locale)
  const [countryCode, setCountryCode] = useState<string>('')
  const [defaultCountry, setDefaultCountry] = useState<number>(0)

  const formMethods = useForm<SampleBookFormSchema>({
    resolver: schemaResolver(assert_text),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: {
      BrancheId: '0',
      Title: '',
      FirstName: '',
      Surname: '',
      Company: '',
      TradeName: '',
      Address1: '',
      Postcode: '',
      Town: '',
      Country: '',
      StateId: '',
      Telephone: '',
      Mobile: '',
      Email: '',
      agb_accepted: false,
      newsletter: false
    }
  })
  const { isDirty, isValid } = formMethods.formState
  const selectedCountryId = formMethods.watch('Country')
  const { data: statesData } = useGetStates(String(selectedCountryId))

  useEffect(() => {
    fetch(`${process.env.NEXT_PUBLIC_GEOLOGICAL_COUNTRY}`)
      .then((response) => response.json())
      .then((data) => {
        setCountryCode(data.country_code)
      })
  }, [])

  useEffect(() => {
    if (!countriesData?.length) return
    const matchedCountry = countriesData.find((country) => country.landCode === countryCode)
    const countryId = matchedCountry?.id ?? countriesData[0].id
    setDefaultCountry(countryId)
  }, [countriesData, countryCode])

  const selectOptions = useMemo(
    () => ({
      country_id: countriesData || [],
      branches: branchesData || [],
      states: statesData || [],
      title: contact_form.personal_informations.salutation_details || []
    }),
    [countriesData, branchesData, statesData, locale]
  )

  useEffect(() => {
    if (defaultCountry) {
      formMethods.setValue('Country', defaultCountry.toString())
    }
  }, [defaultCountry, formMethods])

  /**
   * A suggestion already carries the parsed address, so we fill the address fields
   * directly and validate them, the same as if the user had typed them.
   */
  const handleSelectAddress = useCallback(
    async (suggestion: IAddressSuggestion) => {
      const options = { shouldValidate: true, shouldDirty: true }
      const matchedCountry = countriesData?.find((country) => country.landCode === suggestion.countryCode)

      if (suggestion.address) formMethods.setValue('Address1', suggestion.address, options)
      if (suggestion.postcode) formMethods.setValue('Postcode', format.postcode(suggestion.postcode), options)
      if (suggestion.town) formMethods.setValue('Town', suggestion.town, options)

      if (!matchedCountry) return

      const countryId = String(matchedCountry.id)
      formMethods.setValue('Country', countryId, options)

      // statesData still belongs to the previously selected country, so load the states of the new one
      const states = await queryClient
        .fetchQuery([StorageKey.STATES, countryId], () => getStates(countryId))
        .catch(() => [])

      const matchedState = states?.find(
        (state) => state.name?.trim().toLowerCase() === suggestion.state?.trim().toLowerCase()
      )
      if (matchedState) formMethods.setValue('StateId', String(matchedState.id), options)
    },
    [countriesData, statesData, formMethods]
  )

  const handleSubmitForm = useCallback(
    async (data: SampleBookFormSchema) => {
      try {
        if (!executeRecaptcha) return

        const token = await executeRecaptcha('sample_book_form_submit')

        mutateSampleBook(
          {
            ...data,
            lang: process.env.NEXT_PUBLIC_DEFAULT_LOCALE === 'uk' ? 'en' : process.env.NEXT_PUBLIC_DEFAULT_LOCALE,
            g_recaptcha_token: token
          },
          {
            onSuccess: (response) => {
              if (response.status === 200) {
                routerPush(ROUTER_PATH.REQUEST_THANK_YOU[defaultLocale])
                return
              }

              setFormError(result.error_text)
              setIsModalOpen(true)
            },

            onError: (error: unknown) => {
              const errorMessage = axios.isAxiosError(error) ? error?.response?.data : result.error_text

              setFormError(errorMessage)
              setIsModalOpen(true)
            }
          }
        )
      } catch {
        setFormError(result.error_text)
        setIsModalOpen(true)
      }
    },
    [executeRecaptcha, mutateSampleBook, routerPush, defaultLocale]
  )

  const closeModalWithDelay = useCallback(() => {
    setIsClosing(true)
    formMethods.reset()
    setTimeout(() => {
      setIsModalOpen(false)
      setIsClosing(false)
    }, 1000)
  }, [])

  const directions = [
    { dir: 'column', mq: 'sm' },
    { dir: 'row', mq: 'hd' }
  ]

  return (
    <Row directions={directions}>
      <Col>
        <ControlledForm formMethods={formMethods}>
          <S.ContactForm>
            <Text size='md' weight='bold' margin='0'>
              {contact_form.subheading}
            </Text>
            <Text size='sm' weight='normal' margin='0'>
              <span dangerouslySetInnerHTML={{ __html: contact_form.text }} />
            </Text>
            <Fieldset kind='row' newVersion className='noLegend'>
              <></>
              <S.FieldList fullWidth>
                <Controller
                  name='BrancheId'
                  control={formMethods.control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      isBlock
                      required
                      id='BrancheId'
                      name='BrancheId'
                      placeholder={contact_form.sector.title}
                      options={selectOptions?.branches}
                      value={field.value}
                      onChange={(e) => field.onChange(e.target?.value ?? e)}
                      onBlur={field.onBlur}
                    />
                  )}
                />
              </S.FieldList>
            </Fieldset>

            <Fieldset kind='row' newVersion>
              <Text size='sm' weight='normal' margin='0'>
                {contact_form?.personal_informations?.title}
              </Text>
              <S.FieldList fullWidth>
                <Controller
                  name='Title'
                  control={formMethods.control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      isBlock
                      required
                      id='Title'
                      name='Title'
                      placeholder={contact_form.personal_informations.person_title}
                      options={selectOptions['title']}
                      value={field.value}
                      onChange={(e) => field.onChange(e.target?.value ?? e)}
                      onBlur={field.onBlur}
                    />
                  )}
                />
              </S.FieldList>
            </Fieldset>

            <Fieldset kind='row' newVersion className='noLegend'>
              <></>

              <S.FieldList>
                <ControlledInput
                  {...formMethods.register('FirstName')}
                  isBlock
                  dimension='small'
                  type='text'
                  placeholder={contact_form?.personal_informations?.first_name}
                  value={formMethods.getValues('FirstName')}
                  onChange={(e) => formMethods.setValue('FirstName', e.target.value)}
                />

                <ControlledInput
                  {...formMethods.register('Surname')}
                  isBlock
                  dimension='small'
                  type='text'
                  placeholder={contact_form?.personal_informations?.last_name}
                  value={formMethods.getValues('Surname')}
                  onChange={(e) => formMethods.setValue('Surname', e.target.value)}
                />
              </S.FieldList>
            </Fieldset>

            <Fieldset kind='row' newVersion>
              <Text size='sm' weight='normal' margin='0'>
                {contact_form?.company?.title}
              </Text>
              <S.FieldList isAu={defaultLocale === 'au'}>
                <ControlledInput
                  {...formMethods.register('Company')}
                  isBlock
                  dimension='small'
                  type='text'
                  placeholder={contact_form.company.company_name}
                  value={formMethods.getValues('Company')}
                  onChange={(e) => formMethods.setValue('Company', e.target.value)}
                  className={`company_${defaultLocale}`}
                />

                {defaultLocale !== 'au' && (
                  <ControlledInput
                    {...formMethods.register('TradeName')}
                    isBlock
                    dimension='small'
                    type='text'
                    placeholder={contact_form.company.trade_name}
                    value={formMethods.getValues('TradeName')}
                    onChange={(e) => formMethods.setValue('TradeName', e.target.value)}
                  />
                )}

                <Controller
                  name='Address1'
                  control={formMethods.control}
                  render={({ field, fieldState }) => (
                    <AddressAutocomplete
                      isBlock
                      dimension='small'
                      id='Address1'
                      name='Address1'
                      placeholder={contact_form.company.address}
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value.slice(0, 35))}
                      onBlur={field.onBlur}
                      onSelect={handleSelectAddress}
                      hasError={!!fieldState.error}
                      error={fieldState.error?.message}
                      className={`address1_${defaultLocale}`}
                    />
                  )}
                />

                <ControlledInput
                  {...formMethods.register('Postcode')}
                  isBlock
                  dimension='small'
                  type='text'
                  maxLength={10}
                  formatter={format.postcode}
                  placeholder={contact_form.company.postcode}
                  value={formMethods.getValues('Postcode')}
                  onChange={(e) => formMethods.setValue('Postcode', e.target.value)}
                />

                <ControlledInput
                  {...formMethods.register('Town')}
                  isBlock
                  dimension='small'
                  type='text'
                  placeholder={contact_form?.company?.city}
                  value={formMethods.getValues('Town')}
                  onChange={(e) => formMethods.setValue('Town', e.target.value)}
                />

                <Controller
                  name='Country'
                  control={formMethods.control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      isBlock
                      required
                      id='Country'
                      name='Country'
                      placeholder={contact_form?.company?.country}
                      options={selectOptions.country_id}
                      value={field.value}
                      onChange={(e) => field.onChange(e.target?.value ?? e)}
                      onBlur={field.onBlur}
                      className={`country_${defaultLocale}`}
                    />
                  )}
                />
              </S.FieldList>
            </Fieldset>
            <Fieldset kind='row' newVersion className='noLegend'>
              <></>
              <S.FieldList fullWidth>
                {locale === 'au' && selectOptions.states.length > 0 && (
                  <Controller
                    name='StateId'
                    control={formMethods.control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        isBlock
                        required
                        id='StateId'
                        name='StateId'
                        placeholder={'Please select a state / territory'}
                        options={selectOptions.states}
                        value={field.value}
                        onChange={(e) => field.onChange(e.target?.value ?? e)}
                        onBlur={field.onBlur}
                      />
                    )}
                  />
                )}
              </S.FieldList>
            </Fieldset>
            <Fieldset kind='row' newVersion>
              <Text size='sm' weight='normal' margin='0'>
                {contact_form?.contact?.title}
              </Text>
              <S.FieldList>
                <ControlledInput
                  {...formMethods.register('Telephone')}
                  isBlock
                  dimension='small'
                  type='tel'
                  placeholder={contact_form?.contact?.telephone_number}
                  value={formMethods.getValues('Telephone')}
                  onChange={(e) => formMethods.setValue('Telephone', e.target.value)}
                />

                {defaultLocale !== 'au' && (
                  <ControlledInput
                    {...formMethods.register('Mobile')}
                    isBlock
                    dimension='small'
                    type='tel'
                    placeholder={contact_form?.contact?.mobile}
                    value={formMethods.getValues('Mobile')}
                    onChange={(e) => formMethods.setValue('Mobile', e.target.value)}
                  />
                )}

                <ControlledInput
                  {...formMethods.register('Email')}
                  isBlock
                  dimension='small'
                  type='string'
                  placeholder={contact_form?.contact?.email}
                  value={formMethods.getValues('Email')}
                  onChange={(e) => formMethods.setValue('Email', e.target.value)}
                />
              </S.FieldList>
            </Fieldset>

            <Fieldset kind='row' newVersion className='noLegend'>
              <></>

              <S.FieldList fullWidth>
                <Controller
                  name='agb_accepted'
                  control={formMethods.control}
                  render={({ field }) => (
                    <Checkbox
                      id='agb_accepted'
                      checked={field.value}
                      label={contact_form.data_protection_text}
                      kind='square'
                      onChange={(checked) => field.onChange(checked)}
                      name={'agb_accepted'}
                      value={''}
                    />
                  )}
                />

                {defaultLocale !== 'au' && (
                  <Controller
                    name='newsletter'
                    control={formMethods.control}
                    render={({ field }) => (
                      <Checkbox
                        id='newsletter'
                        checked={field.value}
                        label={contact_form.newsletter_text}
                        kind='square'
                        onChange={(checked) => field.onChange(checked)}
                        name={'newsletter'}
                        value={''}
                      />
                    )}
                  />
                )}
              </S.FieldList>
            </Fieldset>

            <S.FieldsetActions>
              <Button id='refreshButton' kind='white' onClick={() => formMethods.reset()}>
                <Icon name='reload' size='s5' />
                {contact_form.refresh_button}
              </Button>
              <Button
                kind='warning'
                disabled={!isDirty || !isValid}
                onClick={formMethods.handleSubmit(handleSubmitForm)}
                style={{ borderRadius: '2rem' }}
              >
                <Icon name='message' />
                {contact_form.submit_button}
              </Button>
            </S.FieldsetActions>

            {formError && (
              <>
                {isModalOpen && (
                  <div className={`modal ${isClosing ? 'modal-hidden' : 'modal-open'}`}>
                    <Modal
                      size='medium'
                      infos={{
                        subtitle: formError
                      }}
                      close={closeModalWithDelay}
                      func={closeModalWithDelay}
                      cancelButtonName={''}
                      className={'background'}
                      overflow={false}
                    ></Modal>
                  </div>
                )}
              </>
            )}
          </S.ContactForm>
        </ControlledForm>
      </Col>

      <Col>
        <S.SideBoxWrapper>
          <S.FirstSideBox>
            <Image src={first_side_box.main_gif_image.url} width='550' height='200' className='gifImage' />
            <S.headingAligner>
              <Text color='primary' size='xxlg' weight='semiBlack' lineHeight='1.2'>
                <span dangerouslySetInnerHTML={{ __html: first_side_box.heading }} />
              </Text>
              <Image src={first_side_box.side_image.url} alt={first_side_box.side_image.alt} width='70' height='70' />
            </S.headingAligner>
            <Text size='md' margin='1rem 0' weight='semiBlack' lineHeight='1.2'>
              <span dangerouslySetInnerHTML={{ __html: first_side_box.long_text }} />
            </Text>

            <List>
              {first_side_box.list.map(({ key, text, icon }) => (
                <ListItem key={key} iconName={icon as IconName} className='iconCheckmark' fontSize='medium'>
                  {text}
                </ListItem>
              ))}
            </List>
          </S.FirstSideBox>

          <S.SecondSideBox>
            <S.ImageAligner>
              <Text color='primary' size='xxlg' weight='semiBlack' lineHeight='1.2' className='text1'>
                <span dangerouslySetInnerHTML={{ __html: second_side_box.heading }} />
              </Text>
              <Image
                src={second_side_box.second_side_image.url}
                alt={second_side_box.second_side_image.alt}
                width='200'
                height='155'
                className='proofImage'
              />
              <Text size='md' margin='1rem 0' weight='semiBlack' lineHeight='1.2' className='text2'>
                {second_side_box.subheading}
              </Text>
            </S.ImageAligner>

            <Text size='md' margin='1rem 0' lineHeight='1.2' className='text3'>
              {second_side_box.long_text}
            </Text>

            <S.ProofButtonWrapper>
              <Button kind='warning' className='button'>
                <Link href={second_side_box.link} className='proofLink'>
                  <Text weight='bold' margin='0 0 0 0'>
                    {second_side_box.button_text}
                  </Text>
                  <Icon name='arrow-right' />
                </Link>
              </Button>
            </S.ProofButtonWrapper>
          </S.SecondSideBox>
        </S.SideBoxWrapper>
      </Col>
    </Row>
  )
}
